import { appointmentsGraphqlService, clinicsGraphqlService } from '@/services/api';
import { API_CONFIG, getStandardHeaders } from '@/config/envConfig';

export interface Clinic {
  clinic_id: number;
  clinic_name: string;
  patient_clinic_timezone: string;
}

export interface Appointment {
  appointment_id: number;
  clinic_name: string;
  facility_name: string;
  appointment_date: string;
  appointment_start_time: string;
  appointment_end_time: string;
  patient_clinic_data_id: number | null;
}

export interface Facility {
  facility_id: number;
  facility_name: string;
}

export interface CreateAppointmentRequest {
  appointment_dates: string[];
  starting_time: string;
  ending_time: string;
  duration: number;
}

export interface FetchAppointmentsParams {
  selectedClinicId?: number | null;
  selectedFacility?: string;
  facilities?: Facility[];
  filterStartDate?: Date;
  filterEndDate?: Date;
  filterStartTime?: string;
  filterEndTime?: string;
  rowsPerPage: number;
  offset: number;
  useAdvancedFiltering?: boolean;
}

export class AppointmentSlotsService {
  private getAPIHeaders(token: string, facilityId?: number) {
    const headers = {
      ...getStandardHeaders(true, token),
      'priority': 'u=1, i',
      'sec-ch-ua': '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
      'sec-ch-ua-mobile': '?0',
      'sec-ch-ua-platform': '"macOS"'
    };

    if (facilityId) {
      headers['facility_id'] = facilityId.toString();
    }

    return headers;
  }

  async fetchClinics(hasuraToken: string): Promise<Clinic[]> {
    try {
      const response = await clinicsGraphqlService.getAvailableClinics(hasuraToken);
      return response as unknown as Clinic[];
    } catch (error) {
      console.error('Error fetching clinics:', error);
      throw new Error('Failed to fetch clinics');
    }
  }

  async fetchAppointments(hasuraToken: string, params: FetchAppointmentsParams): Promise<Appointment[]> {
    try {
      let response;
      
      // If advanced filtering is requested, use the new filtering API
      if (params.useAdvancedFiltering && (params.filterStartDate || params.filterEndDate || params.filterStartTime || params.filterEndTime)) {
        // Handle facility filtering - support both single facility and all facilities
        let facilityIDs: number[] | null = null;
        if (params.selectedFacility && params.facilities) {
          if (params.selectedFacility === "all" || params.selectedFacility === "All Facilities") {
            // Get all facility IDs when "All Facilities" is selected
            facilityIDs = params.facilities.map(f => f.facility_id);
          } else {
            // Get single facility ID for specific facility selection
            const facility = params.facilities.find(f => f.facility_name === params.selectedFacility);
            facilityIDs = facility ? [facility.facility_id] : null;
          }
        }

        const filters = {
          clinicID: params.selectedClinicId,
          facilityIDs: facilityIDs,
          startDate: params.filterStartDate ? this.formatDateForAPI(params.filterStartDate) : undefined,
          endDate: params.filterEndDate ? this.formatDateForAPI(params.filterEndDate) : undefined,
          startTime: params.filterStartTime ? `${params.filterStartTime}:00` : undefined,
          endTime: params.filterEndTime ? `${params.filterEndTime}:00` : undefined,
        };

        response = await appointmentsGraphqlService.getAdvancedFilteredAppointmentSlots(hasuraToken, filters);
      } else {
        // Use new paginated API with limit and offset
        let facilityIDs: number[] | null = null;
        if (params.selectedFacility && params.facilities) {
          if (params.selectedFacility === "all" || params.selectedFacility === "All Facilities") {
            facilityIDs = params.facilities.map(f => f.facility_id);
          } else {
            const facility = params.facilities.find(f => f.facility_name === params.selectedFacility);
            facilityIDs = facility ? [facility.facility_id] : null;
          }
        }

        const filters = {
          clinicID: params.selectedClinicId,
          facilityIDs: facilityIDs,
          startDate: params.filterStartDate ? this.formatDateForAPI(params.filterStartDate) : undefined,
          endDate: params.filterEndDate ? this.formatDateForAPI(params.filterEndDate) : undefined,
        };

        response = await appointmentsGraphqlService.getPaginatedAppointmentSlots(
          hasuraToken,
          params.rowsPerPage,
          params.offset,
          filters
        );
      }

      return response as unknown as Appointment[];
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw new Error('Failed to fetch appointments');
    }
  }

  async fetchAppointmentsCount(hasuraToken: string, params: FetchAppointmentsParams): Promise<number> {
    try {
      let facilityIDs: number[] | null = null;
      if (params.selectedFacility && params.facilities) {
        if (params.selectedFacility === "all" || params.selectedFacility === "All Facilities") {
          facilityIDs = params.facilities.map(f => f.facility_id);
        } else {
          const facility = params.facilities.find(f => f.facility_name === params.selectedFacility);
          facilityIDs = facility ? [facility.facility_id] : null;
        }
      }

      const filters = {
        clinicID: params.selectedClinicId,
        facilityIDs: facilityIDs,
        startDate: params.filterStartDate ? this.formatDateForAPI(params.filterStartDate) : undefined,
        endDate: params.filterEndDate ? this.formatDateForAPI(params.filterEndDate) : undefined,
      };

      return await appointmentsGraphqlService.getAppointmentSlotsCount(hasuraToken, filters);
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      throw new Error('Failed to fetch appointments count');
    }
  }

  private formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async fetchFacilities(hasuraToken: string, clinicId: number): Promise<Facility[]> {
    if (!clinicId) {
      return [];
    }

    try {
      const response = await clinicsGraphqlService.getFacilitiesByClinic(clinicId, hasuraToken);
      return response as unknown as Facility[];
    } catch (error) {
      
      return [];
    }
  }

  async fetchFacilitiesForAllClinics(hasuraToken: string, clinicIds: number[]): Promise<Facility[]> {
    if (!clinicIds || clinicIds.length === 0) {
      return [];
    }

    try {
      const response = await clinicsGraphqlService.getAvailableFacilitiesByClinic(clinicIds);
      return response as unknown as Facility[];
    } catch (error) {
      
      return [];
    }
  }

  async createAppointmentSlots(
    token: string,
    userId: string,
    facilityId: number,
    appointmentData: CreateAppointmentRequest
  ): Promise<any> {
    if (!token || !userId) {
      throw new Error("User authentication required");
    }

    const response = await fetch(`${API_CONFIG.API_BASE}/api/pep/${userId}/pa/appointment/${facilityId}`, {
      method: 'POST',
      headers: this.getAPIHeaders(token, facilityId),
      body: JSON.stringify(appointmentData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async deleteAppointment(token: string, appointmentId: number, userId?: string): Promise<void> {
    if (!token) {
      throw new Error("No authentication token available");
    }

    // Use provided userId or fallback to a default (this should be passed from the calling code)
    const userIdToUse = userId || '192611';

    const response = await fetch(`${API_CONFIG.API_BASE}/api/pep/${userIdToUse}/pa/appointment/${appointmentId}`, {
      method: 'DELETE',
      headers: {
        ...this.getAPIHeaders(token),
        'content-length': '0',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async deleteAllAppointments(token: string, userId: string, appointmentIds: number[]): Promise<any> {
    if (!token || !userId) {
      throw new Error("User authentication required");
    }

    const response = await fetch(`${API_CONFIG.API_BASE}/api/pep/${userId}/pa/deleteAppointments`, {
      method: 'POST',
      headers: this.getAPIHeaders(token),
      body: JSON.stringify({
        appointmentIds: appointmentIds
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  generateAppointmentDates(startDate: Date, endDate: Date, selectedDays: string[]): string[] {
    const result: string[] = [];
    const dayMap = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const dayIndexes = selectedDays.map(day => dayMap[day as keyof typeof dayMap]);

    // Create new Date objects using the local date components to avoid timezone issues
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

    while (current <= end) {
      if (dayIndexes.includes(current.getDay())) {
        // Use local date components to format YYYY-MM-DD to avoid timezone conversion
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        result.push(`${year}-${month}-${day}`);
      }
      current.setDate(current.getDate() + 1);
    }
    return result;
  }

  validateSlotForm(formData: {
    dialogClinic: string;
    selectedDialogFacility: string;
    selectedDays: string[];
    startDate?: Date;
    endDate?: Date;
    startTime: string;
    endTime: string;
    duration: string;
  }): Record<string, string> {
    const errors: Record<string, string> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const {
      dialogClinic,
      selectedDialogFacility,
      selectedDays,
      startDate,
      endDate,
      startTime,
      endTime,
      duration
    } = formData;

    if (!dialogClinic) errors.clinic = "Please select a clinic";
    if (!selectedDialogFacility) errors.facility = "Please select a facility";
    if (!selectedDays || selectedDays.length === 0) errors.days = "Please select days of week";

    // Enhanced start date validation
    if (!startDate) {
      errors.startDate = "Please select start date";
    } else {
      const startDateCheck = new Date(startDate);
      startDateCheck.setHours(0, 0, 0, 0);
      if (startDateCheck < today) {
        errors.startDate = "Start date cannot be in the past";
      }
    }

    // Enhanced end date validation
    if (!endDate) {
      errors.endDate = "Please select end date";
    } else {
      const endDateCheck = new Date(endDate);
      endDateCheck.setHours(0, 0, 0, 0);
      if (endDateCheck < today) {
        errors.endDate = "End date cannot be in the past";
      } else if (startDate) {
        const startDateCheck = new Date(startDate);
        startDateCheck.setHours(0, 0, 0, 0);
        if (endDateCheck < startDateCheck) {
          errors.endDate = "End date cannot be before start date";
        }
      }
    }

    // Enhanced time validation
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    if (!startTime) {
      errors.startTime = "Please select start time";
    } else if (startDate) {
      const startDateCheck = new Date(startDate);
      startDateCheck.setHours(0, 0, 0, 0);
      const todayCheck = new Date();
      todayCheck.setHours(0, 0, 0, 0);

      // If start date is today, validate that start time is not in the past
      if (startDateCheck.getTime() === todayCheck.getTime() && startTime < currentTime) {
        errors.startTime = "Start time cannot be in the past";
      }
    }

    if (!endTime) {
      errors.endTime = "Please select end time";
    } else if (endDate) {
      const endDateCheck = new Date(endDate);
      endDateCheck.setHours(0, 0, 0, 0);
      const todayCheck = new Date();
      todayCheck.setHours(0, 0, 0, 0);

      // If end date is today, validate that end time is not in the past
      if (endDateCheck.getTime() === todayCheck.getTime() && endTime < currentTime) {
        errors.endTime = "End time cannot be in the past";
      }

      // Validate that end time is after start time if both dates are the same
      if (startDate && endDate && startTime) {
        const startDateCheck = new Date(startDate);
        startDateCheck.setHours(0, 0, 0, 0);
        if (endDateCheck.getTime() === startDateCheck.getTime() && endTime <= startTime) {
          errors.endTime = "End time must be after start time";
        }
      }
    }
    if (!duration || parseInt(duration) <= 0) errors.duration = "Please enter valid duration";

    return errors;
  }
}

export const appointmentSlotsService = new AppointmentSlotsService();